<template>
	<div class="hris-page">
		<div class="hris-page-inner">
			<div class="hris-page-header">
				<span class="hris-page-eyebrow">Timekeeping</span>
				<h1 class="hris-page-title">Attendance</h1>
				<p class="hris-page-subtitle">View and record your daily attendance.</p>
			</div>

			<div class="hris-toolbar">
				<q-select
					v-model="yearfilter"
					:options="yearOptions"
					label="Year"
					outlined
					dense
					color="green"
					style="min-width: 140px; max-width: 200px;"
				></q-select>
				<q-select
					v-model="month"
					:options="monthOptions"
					label="Month"
					emit-value
					map-options
					option-value="id"
					option-label="name"
					outlined
					dense
					color="green"
					style="min-width: 160px; max-width: 200px;"
				></q-select>
				<q-btn unelevated class="hris-q-primary" @click="fetchFromServer(0, 15)" label="Filter"></q-btn>
			</div>

			<div class="hris-action-strip">
				<div
					v-if="isGeofenceEnabled || isGeolocationEnabled"
					class="row items-center q-gutter-sm full-width justify-center"
					style="margin-bottom: 4px;"
				>
					<span
						class="hris-badge"
						:class="{
							'hris-badge-warning': locationStatus.color === 'orange',
							'hris-badge-success': locationStatus.color === 'green',
							'hris-badge-danger': locationStatus.color === 'red',
							'hris-badge-neutral': locationStatus.color === 'grey'
						}"
					>
						{{ locationStatus.label }}
					</span>
					<q-btn
						flat
						dense
						unelevated
						class="hris-q-ghost"
						label="Update Location"
						icon="fas fa-map-marker-alt"
						@click="updateLocation"
						:loading="locating"
					></q-btn>
				</div>

				<q-btn
					v-if="currentAction === 'In'"
					@click="debouncedUploadAttendance('Check In')"
					unelevated
					class="hris-q-primary"
					label="Check In"
					:disable="attendanceActionDisabled"
					:loading="attendanceSubmitting"
				></q-btn>
				<q-btn
					v-if="currentAction === 'Break Out'"
					@click="debouncedUploadAttendance('Break Out')"
					unelevated
					class="hris-q-primary"
					label="Break Out"
					:disable="attendanceActionDisabled"
					:loading="attendanceSubmitting"
				></q-btn>
				<q-btn
					v-if="currentAction === 'Break In'"
					@click="debouncedUploadAttendance('Break In')"
					unelevated
					class="hris-q-primary"
					label="Break In"
					:disable="attendanceActionDisabled"
					:loading="attendanceSubmitting"
				></q-btn>
				<q-btn
					v-if="currentAction === 'Lunch Out'"
					@click="debouncedUploadAttendance('Lunch Out')"
					unelevated
					class="hris-q-primary"
					label="Lunch Out"
					:disable="attendanceActionDisabled"
					:loading="attendanceSubmitting"
				></q-btn>
				<q-btn
					v-if="currentAction === 'Lunch In'"
					@click="debouncedUploadAttendance('Lunch In')"
					unelevated
					class="hris-q-primary"
					label="Lunch In"
					:disable="attendanceActionDisabled"
					:loading="attendanceSubmitting"
				></q-btn>
				<q-btn
					v-if="currentAction === 'Out'"
					@click="debouncedUploadAttendance('Check Out')"
					unelevated
					class="hris-q-primary"
					label="Check Out"
					:disable="attendanceActionDisabled"
					:loading="attendanceSubmitting"
				></q-btn>
				<div v-if="currentAction === 'Done'" class="hris-action-status">
					Attendance completed for today.
				</div>
			</div>

			<div class="hris-panel">
				<q-table
					:dense="$q.screen.lt.md"
					:grid="$q.screen.xs"
					:data="timelogs"
					:columns="columns"
					:pagination.sync="pagination"
					row-key="id"
					separator="cell"
					no-data-label="I didn't find anything for you!"
					:loading="loading"
					:filter="filter"
					@request="onRequest"
					binary-state-sort
					flat
				>
					<template v-slot:body="props">
						<q-tr slot="body" :props="props">
							<q-td key="employee_id" :props="props">{{ props.row.employee_id }}</q-td>
							<q-td key="name" :props="props" class="caps">{{ props.row.name }}</q-td>
							<q-td key="type" :props="props" class="caps">{{ props.row.type }}</q-td>
							<q-td key="time_logs" :props="props">{{ props.row.time_logs }}</q-td>
						</q-tr>
					</template>
					<template v-slot:no-data>
						<div class="hris-empty">
							<span>No attendance records match this filter.</span>
						</div>
					</template>
					<template v-slot:loading>
						<q-inner-loading showing>
							<div class="hris-loading">
								<q-spinner color="green" size="40px"></q-spinner>
								<span>Loading attendance...</span>
							</div>
						</q-inner-loading>
					</template>
				</q-table>
			</div>

			<q-dialog v-model="spinner" persistent>
				<div class="hris-loading" style="background: transparent; min-width: 120px;">
					<q-spinner color="green" size="48px"></q-spinner>
				</div>
			</q-dialog>
		</div>
	</div>
</template>

<script>
import axios from 'axios';
import mixins from '../../mixins';
import { exportFile } from 'quasar';
function wrapCsvValue(val, formatFn) {
	let formatted = formatFn !== void 0 ? formatFn(val) : val

	formatted = formatted === void 0 || formatted === null ? '' : String(formatted)

	formatted = formatted.split('"').join('""')
	return `"${formatted}"`
}
export default {
	data() {
		return {
			lastLog: null,
			lastLogId: null,
			attendancePollTimer: null,
			filter: '',
			loading: false,
			locating: false,
			locationError: false,
			currentPosition: null,
			pagination: {
				page: 1,
				rowsPerPage: 15,
				rowsNumber: 10
			},

			yearfilter: new Date().getFullYear(),
			yearOptions: [],
			cutoff: 1,
			month: new Date().getMonth() + 1,

			importfile: false,
			spinner: false,
			file: '',
			attendanceSubmitting: false,
			attendanceDebounceTimer: null,

			columns: [
				{ name: 'employee_id', align: 'center', label: 'Employee ID', field: 'employee_id', sortable: true },
				{ name: 'type', align: 'center', label: 'Types', field: 'type', sortable: true },
				{ name: 'time_logs', align: 'center', label: 'Date', field: 'time_logs', sortable: true },
			],
				timelogs: [],
				nextLog: null,
			cutoffOptions: [{ id: 1, name: 'First Cut-off' }, { id: 2, name: 'Second Cut-off' }],
			monthOptions: [
				{ id: 1, name: 'January' },
				{ id: 2, name: 'February' },
				{ id: 3, name: 'March' },
				{ id: 4, name: 'April' },
				{ id: 5, name: 'May' },
				{ id: 6, name: 'June' },
				{ id: 7, name: 'July' },
				{ id: 8, name: 'August' },
				{ id: 9, name: 'September' },
				{ id: 10, name: 'October' },
				{ id: 11, name: 'November' },
				{ id: 12, name: 'December' },],
		}
	},

	created() {
		this.yearsList()
		this.checkAttendance()
		this.startAttendancePolling()
	},

	mounted() {
		this.onRequest({
			pagination: this.pagination,
			filter: ''
		})
	},
	beforeDestroy() {
		this.stopAttendancePolling()
		if (this.attendanceDebounceTimer) {
			clearTimeout(this.attendanceDebounceTimer)
			this.attendanceDebounceTimer = null
		}
	},
	beforeUnmount() {
		this.stopAttendancePolling()
		if (this.attendanceDebounceTimer) {
			clearTimeout(this.attendanceDebounceTimer)
			this.attendanceDebounceTimer = null
		}
	},

	computed: {
		isGeofenceEnabled() {
			const enabledFeatures = Array.isArray(this.userData.features) ? this.userData.features : [];
			return enabledFeatures.includes('geofence');
		},
		isGeolocationEnabled() {
			const enabledFeatures = Array.isArray(this.userData.features) ? this.userData.features : [];
			return enabledFeatures.includes('geolocation');
		},
		locationStatus() {
			if (this.locating) return { label: 'Finding Location...', color: 'orange', icon: 'fas fa-spinner fa-spin' };
			if (this.currentPosition) return { label: 'Location Active', color: 'green', icon: 'fas fa-check-circle' };
			if (this.locationError) return { label: 'Location Error', color: 'red', icon: 'fas fa-times-circle' };
			return { label: 'Location Required', color: 'grey', icon: 'fas fa-map-marker-alt' };
		},
		attendanceActionDisabled() {
			return this.attendanceSubmitting || this.locating || this.loading;
		},
		currentAction() {
			if (!this.lastLog) return 'In';
			const type = this.lastLog.type.toLowerCase();
			if (type === 'in' || type === 'check in') return 'Break Out';
			if (type === 'break out') return 'Break In';
			if (type === 'break in') return 'Lunch Out';
			if (type === 'lunch out') return 'Lunch In';
			if (type === 'lunch in') return 'Out';
			if (type === 'out' || type === 'check out') return 'Done'; // this prevents checking in again once cycle is done
			return 'In';
		}
	},

	methods: {
		debouncedUploadAttendance(type) {
			if (this.attendanceActionDisabled) return;
			if (this.attendanceDebounceTimer) clearTimeout(this.attendanceDebounceTimer);
			this.attendanceDebounceTimer = setTimeout(() => {
				this.attendanceDebounceTimer = null;
				this.uploadAttendance(type);
			}, 1000);
		},
		async updateLocation() {
			this.locating = true;
			this.locationError = false;
			try {
				const position = await this.getCurrentPosition();
				this.currentPosition = position;
			} catch (error) {
				this.locationError = true;
				this.$q.notify({
					progress: true,
					message: 'Failed to get location. Please allow access.',
					color: 'red'
				}, 2000);
			} finally {
				this.locating = false;
			}
		},
		getCurrentPosition() {
			return new Promise((resolve, reject) => {
				if (!navigator.geolocation) {
					reject(new Error('Geolocation is not supported.'));
					return;
				}

				navigator.geolocation.getCurrentPosition(
					(position) => resolve(position),
					(error) => reject(error),
					{
						enableHighAccuracy: true,
						timeout: 8000,
						maximumAge: 0
					}
				);
			});
		},

		//start if server side
		async onRequest(props) {
			const { page, rowsPerPage } = props.pagination
			const filter = props.filter
			this.loading = true

			const total = await this.getRowsNumberCount(filter)
			this.pagination.rowsNumber = total

			const fetchCount = rowsPerPage === 0 ? total : rowsPerPage
			const startRow = (page - 1) * rowsPerPage

			this.fetchFromServer(startRow, fetchCount, filter)

			this.pagination.page = page
			this.pagination.rowsPerPage = rowsPerPage
			this.loading = false
		},

		fetchFromServer(startRow, count, filter) {
			axios.get(this.apiUrl + '/timekeeping/attendance', {
				headers: { 'Authorization': `Bearer ${this.token}` },
				params: {
					year: this.yearfilter,
					month: this.month,
					cutoff: this.cutoff,
					start: startRow,
					countPerPage: count,
					filter: filter,
				}
			}).then((response) => {
				this.timelogs = response.data.data
				this.loading = false
			}).catch((error) => {
				console.error(error);
			})
		},

		async getRowsNumberCount(filter) {
			let response = await axios.get(this.apiUrl + '/timekeeping/attendance/total', {
				headers: { 'Authorization': `Bearer ${this.token}` },
				params: {
					year: this.yearfilter,
					month: this.month,
					cutoff: this.cutoff,
					filter: filter,
				}
			})

			return response.data = response.data == 0 ? null : Number(response.data);
		},

		// disable/enable check in/out button based on api response
		checkAttendance() {
			axios.create({
				baseURL: this.apiUrl,
				headers: { 'Authorization': `Bearer ${this.token}` }
			}).post('/timekeeping/check-attendance')
				.then((response) => {
					const incomingLog = response.data.lastLog ?? null
					this.nextLog = response.data.nextType ?? null
					if (incomingLog && incomingLog.id && incomingLog.id !== this.lastLogId) {
						this.lastLogId = incomingLog.id
						this.lastLog = incomingLog
						this.refreshTimelogTable()
					} else if (!incomingLog) {
						this.lastLog = null
						this.lastLogId = null
					} else {
						this.lastLog = incomingLog
					}
				}).catch((error) => {
					console.error(error);
				})
		},

		yearsList: function () {
			axios.create({
				baseURL: this.apiUrl,
				headers: { 'Authorization': `Bearer ${this.token}` }
			}).post(`/timekeeping/years/list`)
				.then((res) => {
					this.yearOptions = res.data.data
				});
		},

		async uploadAttendance(type) {
			if (this.attendanceSubmitting) return;
			this.attendanceSubmitting = true;
			let formData = new FormData();
			formData.append("type", type);

			const geofencedTypes = [
				'in',
				'out',
				'check in',
				'check out',
			];
			const normalizedType = String(type || '').trim().toLowerCase();
			const shouldValidateGeofence = geofencedTypes.includes(normalizedType);

			const needsLocation = (this.isGeofenceEnabled && shouldValidateGeofence) || this.isGeolocationEnabled;
			if (needsLocation) {
				try {
					let position = this.currentPosition;
					if (!position) {
						this.locating = true;
						position = await this.getCurrentPosition();
						this.currentPosition = position;
					}
					formData.append("user_lat", position.coords.latitude);
					formData.append("user_lon", position.coords.longitude);
				} catch (error) {
					if (this.isGeofenceEnabled && shouldValidateGeofence) {
						// Geofencing requires location — block submission
						this.locationError = true;
						this.$q.notify({
							progress: true,
							message: 'Location access is required for attendance.',
							color: 'red'
						}, 2000);
						this.attendanceSubmitting = false;
						return;
					}
					// Geolocation-only: proceed without coordinates (soft fail)
					console.warn('Geolocation capture failed, proceeding without coordinates.');
				} finally {
					this.locating = false;
				}
			}

			this.spinner = true;
			try {
				await axios.create({
					baseURL: this.apiUrl,
					headers: { 'Authorization': `Bearer ${this.token}` }
				}).post('/timekeeping/attendance', formData);

				this.checkAttendance();
				this.onRequest({
					pagination: this.pagination,
					filter: ''
				});
				this.$q.notify({
					progress: true,
					message: `${type} Successful.`,
					color: 'green'
				}, 2000);
			} catch (error) {
				const serverMessage = error?.response?.data?.message;
				const distance = error?.response?.data?.distance;
				const radius = error?.response?.data?.radius;
				let message = serverMessage || 'Something went wrong.';

				if (typeof distance === 'number' && typeof radius === 'number') {
					message = `${message} (Distance: ${distance}m, Allowed: ${radius}m)`;
				}

				this.$q.notify({
					progress: true,
					message,
					color: 'red'
				}, 2000);
				console.error(error?.response?.status || error);
			} finally {
				this.spinner = false;
				this.attendanceSubmitting = false;
			}
		},
		startAttendancePolling() {
			this.stopAttendancePolling()
			this.attendancePollTimer = setInterval(() => {
				this.checkAttendance()
			}, 10000)
		},
		stopAttendancePolling() {
			if (this.attendancePollTimer) {
				clearInterval(this.attendancePollTimer)
				this.attendancePollTimer = null
			}
		},
		refreshTimelogTable() {
			this.onRequest({
				pagination: this.pagination,
				filter: ''
			})
		}
	}
}
</script>
